import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ParticleField, DataStream, MorphBlob, NodeNetwork } from "@/components/effects/DynamicBackgrounds";
import { SEOHead } from "@/components/seo/SEOHead";
import { 
  Factory, 
  ShoppingBag, 
  Radio, 
  Heart, 
  ArrowRight,
  CheckCircle,
  TrendingUp,
  Shield,
  Zap
} from "lucide-react";
import { Button } from "@/components/ui/button";

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const industries = [
  {
    id: "manufacturing",
    name: "Manufacturing",
    icon: Factory,
    color: "#00B4D8",
    description: "Streamline complex supply chains and optimize direct material spend across multiple plants and suppliers.",
    painPoints: [
      "Fragmented supplier base across multiple locations",
      "Difficulty tracking material costs and price fluctuations",
      "Manual contract management for thousands of SKUs",
      "Limited visibility into tier-2 supplier risks"
    ],
    solutions: [
      "Unified spend visibility across all facilities",
      "AI-driven supplier consolidation recommendations",
      "Automated contract compliance monitoring",
      "Predictive analytics for material cost forecasting"
    ],
    outcomes: [
      { metric: "15-20%", description: "Reduction in indirect spend" },
      { metric: "30%", description: "Faster contract processing" },
      { metric: "90%", description: "Improvement in spend visibility" }
    ]
  },
  {
    id: "retail",
    name: "Retail",
    icon: ShoppingBag,
    color: "#10B981",
    description: "Manage seasonal demand fluctuations and optimize vendor relationships for better margins.",
    painPoints: [
      "Seasonal demand creates procurement complexity",
      "Multiple suppliers for same product categories",
      "Price negotiation without data-driven insights",
      "Contract terms vary widely across vendors"
    ],
    solutions: [
      "Demand-driven spend forecasting",
      "Vendor performance benchmarking",
      "AI-powered negotiation insights",
      "Standardized contract templates with risk analysis"
    ],
    outcomes: [
      { metric: "12%", description: "Average cost reduction" },
      { metric: "45%", description: "Faster vendor onboarding" },
      { metric: "2x", description: "Improvement in vendor compliance" }
    ]
  },
  {
    id: "telecom",
    name: "Telecom",
    icon: Radio,
    color: "#8B5CF6",
    description: "Navigate complex technology procurement and manage long-term infrastructure contracts effectively.",
    painPoints: [
      "Complex multi-year technology contracts",
      "Rapid technology obsolescence",
      "Multiple vendors for network infrastructure",
      "Compliance requirements across jurisdictions"
    ],
    solutions: [
      "Contract lifecycle management with renewal tracking",
      "Technology vendor consolidation analysis",
      "Multi-jurisdiction compliance monitoring",
      "Spend optimization across network operations"
    ],
    outcomes: [
      { metric: "25%", description: "Reduction in contract processing time" },
      { metric: "$2M+", description: "Average annual savings identified" },
      { metric: "100%", description: "Contract visibility across regions" }
    ]
  },
  {
    id: "ngo",
    name: "NGOs & Development",
    icon: Heart,
    color: "#EF4444",
    description: "Maximize impact through efficient procurement while maintaining donor compliance and transparency.",
    painPoints: [
      "Donor reporting requirements and compliance",
      "Limited procurement resources and expertise",
      "Need for transparent spending documentation",
      "Managing multiple project-based contracts"
    ],
    solutions: [
      "Automated compliance reporting for donors",
      "Simplified procurement workflows",
      "Complete audit trail for all transactions",
      "Project-based spend tracking and analysis"
    ],
    outcomes: [
      { metric: "40%", description: "Reduction in reporting time" },
      { metric: "95%", description: "Donor compliance rate" },
      { metric: "20%", description: "More funds directed to programs" }
    ]
  }
];

export const IndustriesPage = () => {
  return (
    <div className="bg-[#020617] pt-20">
      <SEOHead
        title="Industries"
        description="ProcureAide delivers procurement intelligence across manufacturing, retail, telecommunications and healthcare — tailored to each sector's unique challenges."
        path="/industries"
      />
      {/* Hero */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern-animated opacity-30" />
        <ParticleField count={20} />
        <DataStream count={5} className="opacity-25" />
        <MorphBlob color="rgba(0,180,216,0.06)" size="500px" className="top-[5%] right-[-10%]" />
        <div className="absolute top-1/3 left-0 w-[500px] h-[500px] bg-[#00B4D8]/5 rounded-full blur-[100px]" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="text-center max-w-3xl mx-auto"
          >
            <motion.p 
              variants={fadeInUp}
              className="text-xs uppercase tracking-[0.2em] text-[#00B4D8] mb-4"
            >
              Industries We Serve
            </motion.p>
            <motion.h1 
              variants={fadeInUp}
              className="text-4xl sm:text-5xl font-bold text-[#F8FAFC] leading-tight tracking-tight mb-6"
            >
              Procurement Intelligence for{" "}
              <span className="text-[#00B4D8]">Every Industry</span>
            </motion.h1>
            <motion.p 
              variants={fadeInUp}
              className="text-lg text-[#94A3B8]"
            >
              Whether you're in manufacturing, retail, telecom, or the development sector, 
              Procureaide adapts to your unique procurement challenges.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-24">
            {industries.map((industry, index) => (
              <motion.div
                key={industry.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="relative"
                id={industry.id}
              >
                {/* Industry Header */}
                <div className="flex items-center gap-4 mb-8">
                  <div 
                    className="w-16 h-16 rounded-2xl flex items-center justify-center"
                    style={{ backgroundColor: `${industry.color}15` }}
                  >
                    <industry.icon className="w-8 h-8" style={{ color: industry.color }} />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold text-[#F8FAFC]">{industry.name}</h2>
                    <p className="text-[#94A3B8]">{industry.description}</p>
                  </div>
                </div>

                <div className="grid lg:grid-cols-3 gap-8">
                  {/* Pain Points */}
                  <div className="bg-[#0B1426] border border-[#EF4444]/20 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-[#EF4444] mb-4 flex items-center gap-2">
                      <Shield className="w-5 h-5" />
                      Pain Points
                    </h3>
                    <ul className="space-y-3">
                      {industry.painPoints.map((point, i) => (
                        <li key={i} className="flex items-start gap-3 text-sm text-[#94A3B8]">
                          <div className="w-1.5 h-1.5 rounded-full bg-[#EF4444] mt-2 flex-shrink-0" />
                          {point}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Solutions */}
                  <div className="bg-[#0B1426] border border-[#00B4D8]/20 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-[#00B4D8] mb-4 flex items-center gap-2">
                      <Zap className="w-5 h-5" />
                      Platform Solutions
                    </h3>
                    <ul className="space-y-3">
                      {industry.solutions.map((solution, i) => (
                        <li key={i} className="flex items-start gap-3 text-sm text-[#94A3B8]">
                          <CheckCircle className="w-4 h-4 text-[#00B4D8] mt-0.5 flex-shrink-0" />
                          {solution}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Outcomes */}
                  <div className="bg-[#0B1426] border border-[#10B981]/20 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-[#10B981] mb-4 flex items-center gap-2">
                      <TrendingUp className="w-5 h-5" />
                      Expected Outcomes
                    </h3>
                    <div className="space-y-4">
                      {industry.outcomes.map((outcome, i) => (
                        <div key={i}>
                          <div className="text-2xl font-bold" style={{ color: industry.color }}>
                            {outcome.metric}
                          </div>
                          <p className="text-sm text-[#94A3B8]">{outcome.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#0B1426]/50 relative overflow-hidden">
        <NodeNetwork count={8} className="opacity-20" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2 
              variants={fadeInUp}
              className="text-3xl sm:text-4xl font-bold text-[#F8FAFC] tracking-tight mb-4"
            >
              Don't See Your Industry?
            </motion.h2>
            <motion.p 
              variants={fadeInUp}
              className="text-[#94A3B8] mb-8"
            >
              Procureaide's flexible platform adapts to any industry. 
              Let's discuss how we can solve your unique challenges.
            </motion.p>
            <motion.div variants={fadeInUp}>
              <a href="mailto:info@procureaide.com">
                <Button 
                  size="lg"
                  className="bg-[#00B4D8] text-[#020617] font-bold hover:bg-[#0096B4] hover:scale-105 transition-all shadow-[0_0_20px_rgba(0,180,216,0.3)] px-8"
                  data-testid="industries-cta-btn"
                >
                  Talk to Us
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default IndustriesPage;
